// =============================================================================
// FILE: packages/renderer/src/hooks/useSmartIngestion.ts
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

export const SIE_ROLE_TAXONOMY = [
  'client_pm','telecom_consultant','architect',
  'electrical_engineer','mechanical_engineer','structural_engineer',
  'general_contractor','site_superintendent','project_coordinator',
  'owner','vendor','manufacturer','other',
] as const;
export type SieRole = typeof SIE_ROLE_TAXONOMY[number];

export type DetectionKind =
  | 'organization' | 'contact' | 'email' | 'phone'
  | 'project' | 'meeting' | 'date' | 'address'
  | 'url' | 'sharepoint_link' | 'action_item' | 'note';

export interface Detection {
  id: string;
  kind: DetectionKind;
  payload: Record<string, unknown>;
  confidence: number;
  source: string;
  span: { start: number; end: number };
  status: 'new' | 'existing' | 'duplicate' | 'ignored' | 'applied' | 'failed';
  target: string | null;
  extractor: string;
}

export interface IngestionDraft {
  id: string;
  sourceKind: 'paste' | 'txt' | 'eml' | 'clipboard' | 'folder';
  sourceText: string;
  targetProject: string | null;
  detections: Detection[];
  detectionCount: number;
  status: 'draft' | 'applied' | 'discarded';
  createdAt: number;
  appliedAt: number | null;
  appliedSummary: AppliedSummary | null;
}

export interface AppliedSummary {
  organizationsCreated: number;
  contactsCreated:      number;
  contactsLinked:       number;
  meetingsCreated:      number;
  notesCreated:         number;
  errors:               string[];
}

export interface ApprovalDecision {
  detectionId: string;
  accept: boolean;
  payload?: Record<string, unknown>;
  role?: SieRole;
}

export function useSieParse() {
  return useMutation<IngestionDraft, Error, { text: string; targetProject?: string; sourceKind?: IngestionDraft['sourceKind'] }>({
    mutationFn: (v) => call(IPC.SIE_PARSE, { sourceKind: v.sourceKind ?? 'paste', text: v.text, targetProject: v.targetProject }),
  });
}

export function useSieApply() {
  const qc = useQueryClient();
  return useMutation<AppliedSummary, Error, { id: string; targetProjectId?: string; approvals: ApprovalDecision[] }>({
    mutationFn: (v) => call(IPC.SIE_APPLY_DRAFT, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sie-drafts'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useSieListDrafts(status?: 'draft' | 'applied' | 'discarded') {
  return useQuery<IngestionDraft[]>({
    queryKey: ['sie-drafts', status ?? 'all'],
    queryFn:  () => call(IPC.SIE_LIST_DRAFTS, status ? { status } : {}),
  });
}

export function useSieDiscard() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => call(IPC.SIE_DISCARD_DRAFT, { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sie-drafts'] }),
  });
}
